import React, { useState, useEffect, useRef } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import MarkdownContent from './MarkdownContent';
import ThinkingOrb from '../signature/ThinkingOrb';

const ReasoningBlock = ({ reasoning, isThinking, autoCollapse }) => {
  const [isOpen, setIsOpen] = useState(!autoCollapse);
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (isThinking) {
      if (!startTimeRef.current) startTimeRef.current = Date.now();
      const tick = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
      return () => clearInterval(tick);
    } else if (startTimeRef.current) {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      startTimeRef.current = null;
    }
  }, [isThinking]);

  useEffect(() => {
    if (autoCollapse && isOpen) {
      const timer = setTimeout(() => setIsOpen(false), 800);
      return () => clearTimeout(timer);
    }
  }, [autoCollapse, isOpen]);

  const formatTime = (s) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-obsidian-800 border border-obsidian-600 rounded-2xl overflow-hidden">
        <CollapsibleTrigger className="w-full p-3.5 flex items-center justify-between hover:bg-white/[0.02] transition-all duration-200">
          <div className="flex items-center gap-3 text-white font-medium text-sm">
            {isThinking ? (
              <ThinkingOrb size={24} />
            ) : (
              <div className="w-6 h-6 rounded-full bg-obsidian-700 flex items-center justify-center">
                <span className="w-1.5 h-1.5 bg-lime-500 rounded-full" />
              </div>
            )}
            <span>思考過程</span>
            {isThinking ? (
              <span className="label-micro text-lime-500 tabular-nums">
                {formatTime(elapsed)}
              </span>
            ) : elapsed > 0 ? (
              <span className="label-micro text-obsidian-300 tabular-nums">耗時 {formatTime(elapsed)}</span>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5">
            {!isThinking && (
              <span className="label-micro text-obsidian-400">{isOpen ? '收起' : '展開'}</span>
            )}
            {isOpen
              ? <ChevronDown className="w-3.5 h-3.5 text-obsidian-300" strokeWidth={2} />
              : <ChevronRight className="w-3.5 h-3.5 text-obsidian-300" strokeWidth={2} />}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 text-obsidian-100 text-sm border-t border-obsidian-600 pt-3">
            <MarkdownContent content={reasoning} />
            {isThinking && <span className="animate-pulse text-lime-500">▋</span>}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default ReasoningBlock;
